import React from 'react';
import { FieldValues, Path } from 'react-hook-form';
import Input from '../../Input'; // Your original Input component
import { useFormType, TypedInputProps, useRequiredFields } from './ZodFormProvider';

// Extending your original InputProps but making name type-safe
interface ZodInputComponentProps<T extends FieldValues> extends Omit<React.ComponentProps<typeof Input>, 'name' | 'required'>, TypedInputProps<T> {
    required?: boolean; // Optional override
}

// Generic component that enforces type safety for field names
function ZodInput<T extends FieldValues = FieldValues>({
    name,
    required: requiredOverride,
    ...rest
}: ZodInputComponentProps<T>) {
    // This is only used to infer the type
    useFormType<T>();
    
    // Get required fields information from context
    const requiredFields = useRequiredFields();
    
    // Determine if field is required from schema, but allow override
    const isRequired = requiredOverride !== undefined 
        ? requiredOverride 
        : requiredFields.has(name as string);

    // Pass the isRequired flag as the required prop to the Input component
    // This ensures the asterisk will be displayed appropriately
    return <Input name={name} required={isRequired} {...rest} className={'normal-text'} />;
}

// Helper function to create a typed input for a specific form type
export function createZodInput<T extends FieldValues>() {
    return function TypedInputWithGeneric(props: ZodInputComponentProps<T>) {
        return <ZodInput<T> {...props} />;
    };
}

export default ZodInput;